import type { WordEntry } from "./dialog-data";

type PartOfSpeech = "noun" | "verb" | "adjective" | "adverb" | "pronoun" | "preposition" | "conjunction" | "interjection" | "article" | "particle" | "other";

export interface WordModule {
  id: string;
  label: string;
  icon: "noun" | "verb" | "descriptor" | "other" | "all";
  words: WordEntry[];
  posCategory: PartOfSpeech | "mixed" | "all";
}

type WordState = Record<string, { known?: boolean; tough?: boolean; liked?: boolean }>;

const POS_CONFIG: Record<string, { icon: WordModule["icon"]; order: number }> = {
  noun: { icon: "noun", order: 0 },
  verb: { icon: "verb", order: 1 },
  descriptor: { icon: "descriptor", order: 2 },
  other: { icon: "other", order: 3 },
};

function posGroupKey(pos: PartOfSpeech): string {
  if (pos === "adjective" || pos === "adverb") return "descriptor";
  if (pos === "noun" || pos === "verb") return pos;
  return "other";
}

function posLabel(key: string, count: number): string {
  const labels: Record<string, string> = {
    noun: "Nouns",
    verb: "Verbs",
    descriptor: "Descriptors",
    other: "Other",
    mixed: "Mixed",
  };
  return `${labels[key] || key} (${count})`;
}

export function buildWordModules(words: WordEntry[], wordState?: WordState): WordModule[] {
  if (words.length === 0) return [];

  // Filter out known words for practice purposes
  const practiceWords = wordState
    ? words.filter((w) => !wordState[w.baseForm.toLowerCase()]?.known)
    : words;

  // If total words <= 12, return just one "All Words" module
  if (practiceWords.length <= 12) {
    return [{
      id: "all",
      label: `All Words (${practiceWords.length})`,
      icon: "all",
      words: practiceWords,
      posCategory: "all",
    }];
  }

  // Group by POS
  const groups: Record<string, WordEntry[]> = {};
  for (const word of practiceWords) {
    const pos = (word.partOfSpeech?.toLowerCase().trim() || "other") as PartOfSpeech;
    const key = posGroupKey(pos);
    if (!groups[key]) groups[key] = [];
    groups[key].push(word);
  }

  const modules: WordModule[] = [];

  // Collect tiny groups (<4 words) into "mixed"
  const tinyWords: WordEntry[] = [];
  const groupKeys = Object.keys(groups);

  for (const key of groupKeys) {
    const group = groups[key];
    if (!group) continue;
    if (group.length < 4) {
      tinyWords.push(...group);
      delete groups[key];
    }
  }

  if (tinyWords.length > 0) {
    groups["mixed"] = tinyWords;
  }

  // Build modules from groups, splitting large ones
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const orderA = POS_CONFIG[a]?.order ?? 99;
    const orderB = POS_CONFIG[b]?.order ?? 99;
    return orderA - orderB;
  });

  for (const key of sortedKeys) {
    const groupWords = groups[key];
    if (!groupWords) continue;
    const config = POS_CONFIG[key] || { icon: "other" as const, order: 99 };
    const posCategory = key === "mixed" ? "mixed" as const
      : key === "descriptor" ? "adjective" as PartOfSpeech
      : key as PartOfSpeech;

    if (groupWords.length > 12) {
      // Split into subgroups of ~10
      const chunkSize = 10;
      const chunks = [];
      for (let i = 0; i < groupWords.length; i += chunkSize) {
        chunks.push(groupWords.slice(i, i + chunkSize));
      }
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i]!;
        modules.push({
          id: `${key}-${i + 1}`,
          label: `${posLabel(key, chunk.length)} ${i + 1}/${chunks.length}`,
          icon: config.icon,
          words: chunk,
          posCategory,
        });
      }
    } else {
      modules.push({
        id: key,
        label: posLabel(key, groupWords.length),
        icon: config.icon,
        words: groupWords,
        posCategory,
      });
    }
  }

  // Append "All Words" module at the end
  modules.push({
    id: "all",
    label: `All Words (${practiceWords.length})`,
    icon: "all",
    words: practiceWords,
    posCategory: "all",
  });

  return modules;
}
